import { FC } from "react"
import { GripVertical, Plus, Trash2 } from "lucide-react"
import { ConfigAdminPanel } from "../../../../../config-admin-panel"
import {
  RateTableConfigDetails,
  MortgageTerm,
  LoanType
} from "../../../../../../../../types"

interface Props {
  values: RateTableConfigDetails
  setDetailsField: (field: string, value: any) => void
}

const MORTGAGE_TYPE_OPTIONS: MortgageTerm["type"][] = ["fixed", "arm"]

const RateTableTerms: FC<Props> = ({ values, setDetailsField }) => {
  const d = values.details
  const mortgageTerms: MortgageTerm[] = d.mortgage_terms ?? []
  const loanTypes: LoanType[] = d.loan_type ?? []

  // --- Mortgage terms helpers ---
  const updateMortgageTerm = (
    index: number,
    field: keyof MortgageTerm,
    value: string
  ) => {
    const next = mortgageTerms.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    )
    setDetailsField("mortgage_terms", next)
  }

  const removeMortgageTerm = (index: number) => {
    setDetailsField(
      "mortgage_terms",
      mortgageTerms.filter((_, i) => i !== index)
    )
  }

  const addMortgageTerm = () => {
    setDetailsField("mortgage_terms", [
      ...mortgageTerms,
      { type: "fixed", term: "", label: "" }
    ])
  }

  const moveMortgageTerm = (index: number, direction: -1 | 1) => {
    const next = [...mortgageTerms]
    const target = index + direction
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    setDetailsField("mortgage_terms", next)
  }

  // --- Loan type helpers ---
  const updateLoanType = (
    index: number,
    field: keyof LoanType,
    value: string
  ) => {
    const next = loanTypes.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    )
    setDetailsField("loan_type", next)
  }

  const removeLoanType = (index: number) => {
    setDetailsField(
      "loan_type",
      loanTypes.filter((_, i) => i !== index)
    )
  }

  const addLoanType = () => {
    setDetailsField("loan_type", [...loanTypes, { value: "", label: "" }])
  }

  return (
    <div className="space-y-8 pt-2">
      {/* Mortgage Terms */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">
              Mortgage terms
            </h3>
            <p className="text-xs text-gray-400">
              Available loan term options shown in the embed
            </p>
          </div>
          <button
            type="button"
            onClick={addMortgageTerm}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <Plus size={12} />
            Add term
          </button>
        </div>

        <ConfigAdminPanel className="divide-y divide-gray-100">
          {/* Header row */}
          <div className="grid grid-cols-[32px_90px_80px_1fr_32px] gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <span />
            <span>Type</span>
            <span>Term</span>
            <span>Label</span>
            <span />
          </div>

          {mortgageTerms.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-400 text-center">
              No mortgage terms — add one above
            </div>
          )}

          {mortgageTerms.map((term, i) => (
            <div
              key={i}
              className="grid grid-cols-[32px_90px_80px_1fr_32px] gap-2 items-center px-3 py-2 hover:bg-gray-50 transition-colors"
            >
              {/* Reorder */}
              <div className="flex flex-col items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => moveMortgageTerm(i, -1)}
                  disabled={i === 0}
                  className="text-gray-300 hover:text-gray-500 disabled:opacity-30 transition-colors leading-none"
                  title="Move up"
                >
                  ▲
                </button>
                <GripVertical size={12} className="text-gray-300" />
                <button
                  type="button"
                  onClick={() => moveMortgageTerm(i, 1)}
                  disabled={i === mortgageTerms.length - 1}
                  className="text-gray-300 hover:text-gray-500 disabled:opacity-30 transition-colors leading-none"
                  title="Move down"
                >
                  ▼
                </button>
              </div>

              {/* Type */}
              <select
                value={term.type}
                onChange={e => updateMortgageTerm(i, "type", e.target.value)}
                className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                {MORTGAGE_TYPE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              {/* Term */}
              <input
                type="text"
                value={term.term}
                onChange={e => updateMortgageTerm(i, "term", e.target.value)}
                placeholder="30"
                className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />

              {/* Label */}
              <input
                type="text"
                value={term.label}
                onChange={e => updateMortgageTerm(i, "label", e.target.value)}
                placeholder="30yr Fixed"
                className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />

              {/* Remove */}
              <button
                type="button"
                onClick={() => removeMortgageTerm(i)}
                className="flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </ConfigAdminPanel>
      </section>

      {/* Loan Types */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Loan types</h3>
            <p className="text-xs text-gray-400">
              Loan type filter options shown in the embed
            </p>
          </div>
          <button
            type="button"
            onClick={addLoanType}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <Plus size={12} />
            Add type
          </button>
        </div>

        <ConfigAdminPanel className="divide-y divide-gray-100">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_1fr_32px] gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <span>Value</span>
            <span>Label</span>
            <span />
          </div>

          {loanTypes.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-400 text-center">
              No loan types — add one above
            </div>
          )}

          {loanTypes.map((lt, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center px-3 py-2 hover:bg-gray-50 transition-colors"
            >
              <input
                type="text"
                value={lt.value}
                onChange={e => updateLoanType(i, "value", e.target.value)}
                placeholder="conventional"
                className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="text"
                value={lt.label}
                onChange={e => updateLoanType(i, "label", e.target.value)}
                placeholder="Conventional"
                className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => removeLoanType(i)}
                className="flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </ConfigAdminPanel>
      </section>
    </div>
  )
}

export default RateTableTerms
